import ApiClient from "@/domain/core/utils/apiClient";
import client_core from "@/services/clients/client_core";

const client = new ApiClient(client_core)

const endpoints = {
    // Transactions (admin)
    transactions: '/dashboard/api/v3/accounting/{scope}/transactions/',
    transaction_export: '/dashboard/api/v3/accounting/{scope}/transactions/export/',
    user_transactions: '/dashboard/api/v3/accounting/{scope}/transactions/user-transactions/',
    user_checkins: '/dashboard/api/v3/accounting/{scope}/transactions/user-checkins/',

    // Movements (CM/PS)
    movements: '/dashboard/api/v3/accounting/{scope}/movements/',
    movements_totals: '/dashboard/api/v3/accounting/{scope}/movements/totals/',
    movement_export: '/dashboard/api/v3/accounting/{scope}/movements/export/',
    movement_order_item_detail: '/dashboard/api/v3/accounting/{scope}/movements/order-items/{id}/',
    movement_refund_detail: '/dashboard/api/v3/accounting/{scope}/movements/refunds/{id}/',

    // Source details
    order_item_detail: '/dashboard/api/v3/accounting/{scope}/order-items/{id}/',
    order_detail: '/dashboard/api/v3/accounting/{scope}/orders/{id}/',
    service_order_detail: '/dashboard/api/v3/accounting/{scope}/service-orders/{id}/',
    refund_detail: '/dashboard/api/v3/accounting/{scope}/refunds/{id}/',
    payout_detail: '/dashboard/api/v3/accounting/{scope}/payouts/{id}/',
    payout_ps_download: '/dashboard/api/v3/accounting/{scope}/payouts/{id}/pica-service/{psId}/download/',

    // Order item media (transactions)
    order_item_media: '/dashboard/api/v3/accounting/{scope}/order-items/{orderItemId}/media/',
    checkin_media: '/dashboard/api/v3/accounting/{scope}/order-items/{orderItemId}/checkin-media/',
    download_media: '/dashboard/api/v3/accounting/{scope}/order-items/{orderItemId}/download-media/',

    // Order item media (movements — thumb only, no checkin/download)
    movement_order_item_media: '/dashboard/api/v3/accounting/{scope}/movements/order-items/{orderItemId}/media/',

    // Refund creation (per order item)
    refund_create: '/dashboard/api/v3/accounting/{scope}/order-items/{orderItemId}/refund/',
    refund_calculate: '/dashboard/api/v3/accounting/{scope}/order-items/{orderItemId}/calculate-refund/',

    // Chargebacks
    chargebacks: '/dashboard/api/v3/accounting/{scope}/chargebacks/',
    chargeback_detail: '/dashboard/api/v3/accounting/{scope}/chargebacks/{id}/',
    chargeback_export: '/dashboard/api/v3/accounting/global/chargebacks/export/',
    chargeback_search: '/dashboard/api/v3/accounting/global/chargebacks/search/',
    chargeback_create: '/dashboard/api/v3/accounting/global/chargebacks/',
}

/**
 * Build scope path segment from scope object
 * @param {Object} scope - { type: 'global'|'contract-manager'|'pica-service', id?: number }
 * @returns {string} - Scope path segment (e.g., 'global', 'contract-manager/123', 'pica-service/456')
 */
const buildScope = (scope) => {
    if (!scope || !scope.type) {
        throw new Error('Scope is required')
    }

    if (scope.type === 'global') {
        return 'global'
    } else if (scope.type === 'contract-manager') {
        if (!scope.id) throw new Error('Contract manager ID is required')
        return `contract-manager/${scope.id}`
    } else if (scope.type === 'pica-service') {
        if (!scope.id) throw new Error('Pica service ID is required')
        return `pica-service/${scope.id}`
    }

    throw new Error(`Invalid scope type: ${scope.type}`)
}

/**
 * Build endpoint URL with scope
 */
const buildUrl = (endpoint, scope, replacements = {}) => {
    let url = endpoint.replace('{scope}', buildScope(scope))
    Object.entries(replacements).forEach(([key, value]) => {
        url = url.replace(`{${key}}`, value)
    })
    return url
}

export default {
    // ==================== Transactions (admin) ====================

    /**
     * Fetch list of transactions with filters and pagination
     */
    async fetchTransactionList(scope, filters, pagination, sorting) {
        const url = buildUrl(endpoints.transactions, scope)
        const params = {
            ...filters,
            page_size: pagination.page_size || 20,
            page: pagination.page || 1,
        }

        if (sorting.column) {
            params.ordering = sorting.asc ? sorting.column : `-${sorting.column}`
        }

        const result = await client.get(url, params)

        return {
            results: result.results,
            pagination: {
                num_pages: result.pagination.num_pages,
                page_size: result.pagination.page_size,
                page: result.pagination.page_number,
                total: result.pagination.total,
            }
        }
    },

    /**
     * Export transactions
     */
    async exportTransactions(scope, filters) {
        const url = buildUrl(endpoints.transaction_export, scope)
        return await client.download(url, filters)
    },

    // ==================== Movements (CM/PS) ====================

    /**
     * Fetch list of movements with filters and pagination
     */
    async fetchMovementList(scope, filters, pagination, sorting) {
        const url = buildUrl(endpoints.movements, scope)
        const params = {
            ...filters,
            page_size: pagination.page_size || 20,
            page: pagination.page || 1,
        }

        if (sorting.column) {
            params.ordering = sorting.asc ? sorting.column : `-${sorting.column}`
        }

        const result = await client.get(url, params)

        return {
            results: result.results,
            pagination: {
                num_pages: result.pagination.num_pages,
                page_size: result.pagination.page_size,
                page: result.pagination.page_number,
                total: result.pagination.total,
            }
        }
    },

    /**
     * Fetch aggregated totals for movements (pica-service scope only)
     */
    async fetchMovementsTotals(scope, filters) {
        const url = buildUrl(endpoints.movements_totals, scope)
        return await client.get(url, filters)
    },

    /**
     * Export movements
     */
    async exportMovements(scope, filters) {
        const url = buildUrl(endpoints.movement_export, scope)
        return await client.download(url, filters)
    },

    /**
     * Fetch movement order item detail (simplified, with stakeholder_summary)
     */
    async fetchMovementOrderItem(scope, id) {
        const url = buildUrl(endpoints.movement_order_item_detail, scope, { id })
        return await client.get(url)
    },

    /**
     * Fetch movement refund detail (simplified, with stakeholder_summary)
     */
    async fetchMovementRefund(scope, id) {
        const url = buildUrl(endpoints.movement_refund_detail, scope, { id })
        return await client.get(url)
    },

    // ==================== Source details ====================

    /**
     * Fetch Order Item detail with embedded order info, sibling items, refund data
     */
    async fetchOrderItem(scope, id) {
        const url = buildUrl(endpoints.order_item_detail, scope, { id })
        return await client.get(url)
    },

    /**
     * Fetch Order detail with accounting info (admin OrderDetail page)
     */
    async fetchOrder(scope, id) {
        const url = buildUrl(endpoints.order_detail, scope, { id })
        return await client.get(url)
    },

    /**
     * Fetch ServiceOrder detail with accounting info
     */
    async fetchServiceOrder(scope, id) {
        const url = buildUrl(endpoints.service_order_detail, scope, { id })
        return await client.get(url)
    },

    /**
     * Fetch Refund detail with accounting info
     */
    async fetchRefund(scope, id) {
        const url = buildUrl(endpoints.refund_detail, scope, { id })
        return await client.get(url)
    },

    /**
     * Fetch Payout detail with accounting info
     * Note: Only available for global and contract-manager scopes
     */
    async fetchPayout(scope, id) {
        if (scope.type === 'pica-service') {
            throw new Error('Payout is not available for pica-service scope')
        }
        const url = buildUrl(endpoints.payout_detail, scope, { id })
        return await client.get(url)
    },

    async downloadPayoutPicaService(scope, payoutId, psId) {
        if (scope.type === 'pica-service') {
            throw new Error('Payout is not available for pica-service scope')
        }
        const url = buildUrl(endpoints.payout_ps_download, scope, { id: payoutId, psId })
        return await client.download(url)
    },

    /**
     * Fetch media for a movement order item (thumb only)
     */
    async fetchMovementOrderItemMedia(scope, orderItemId, afterId = null, pageSize = 50) {
        const url = buildUrl(endpoints.movement_order_item_media, scope, { orderItemId })
        const params = { page_size: pageSize }
        if (afterId) {
            params.after_id = afterId
        }
        return await client.get(url, params)
    },

    /**
     * Fetch media for a specific order item
     */
    async fetchOrderItemMedia(scope, orderItemId, afterId = null, pageSize = 50) {
        const url = buildUrl(endpoints.order_item_media, scope, { orderItemId })
        const params = { page_size: pageSize }
        if (afterId) {
            params.after_id = afterId
        }
        return await client.get(url, params)
    },

    /**
     * Fetch all checkin media for an order item
     */
    async fetchCheckinMedia(scope, orderItemId, afterId = null, pageSize = 50) {
        const url = buildUrl(endpoints.checkin_media, scope, { orderItemId })
        const params = { page_size: pageSize }
        if (afterId) {
            params.after_id = afterId
        }
        return await client.get(url, params)
    },

    /**
     * Download media for an order item
     * @param {Object} scope - Scope object
     * @param {number} orderItemId - Order item ID
     * @param {Array} mediaIds - Array of media IDs to download
     * @param {number} page - Page number for chunking (optional)
     * @param {number} limit - Max items per batch, max 500 (optional)
     * @returns {Promise<{files: Array<{url: string, filename: string}>, status: string}>}
     */
    async downloadMedia(scope, orderItemId, mediaIds, page = null, limit = null) {
        const url = buildUrl(endpoints.download_media, scope, { orderItemId })
        const body = { media_ids: mediaIds }
        if (page) body.page = page
        if (limit) body.limit = limit
        return await client.post(url, body)
    },

    /**
     * Fetch transactions for a specific user
     */
    async fetchUserTransactions(scope, userId, excludeTransactionId, limit = 5) {
        const url = buildUrl(endpoints.user_transactions, scope)
        const params = {
            user_id: userId,
            limit,
        }
        if (excludeTransactionId) {
            params.exclude_id = excludeTransactionId
        }
        return await client.get(url, params)
    },

    /**
     * Fetch checkins for a specific user
     */
    async fetchUserCheckins(scope, userId, limit = 5) {
        const url = buildUrl(endpoints.user_checkins, scope)
        const params = {
            user_id: userId,
            limit,
        }
        return await client.get(url, params)
    },

    /**
     * Create a new refund for an order item (backend handles proportional split)
     * @param {Object} scope - Scope object
     * @param {number} orderItemId - Order Item ID
     * @param {Object} data - { amount, refund_type, reason, removed_media_ids? }
     */
    async createRefund(scope, orderItemId, data) {
        const url = buildUrl(endpoints.refund_create, scope, { orderItemId })
        return await client.post(url, data)
    },

    /**
     * Calculate refund amount based on removed media (tier pricing)
     * @param {Object} scope - Scope object
     * @param {number} orderItemId - Order Item ID
     * @param {Array} removedMediaIds - Array of media IDs to remove
     * @returns {Promise<{refund_amount: string, original_price: string, recalculated_price: string, remaining_photo_count: number}>}
     */
    async calculateRefundAmount(scope, orderItemId, removedMediaIds) {
        const url = buildUrl(endpoints.refund_calculate, scope, { orderItemId })
        return await client.post(url, { photo_ids: removedMediaIds })
    },

    // ==================== Chargebacks ====================

    /**
     * Fetch list of chargebacks with optional filters
     */
    async fetchChargebacks(scope, filters = {}, pagination = {}, sorting = {}) {
        const url = buildUrl(endpoints.chargebacks, scope)
        const params = {
            ...filters,
            page_size: pagination.page_size || 20,
            page: pagination.page || 1,
        }

        if (sorting.column) {
            params.ordering = sorting.asc ? sorting.column : `-${sorting.column}`
        }

        const result = await client.get(url, params)
        return {
            results: result.results,
            pagination: {
                num_pages: result.pagination?.num_pages || 1,
                page_size: result.pagination?.page_size || 20,
                page: result.pagination?.page_number || 1,
                total: result.pagination?.total || result.results?.length || 0,
            }
        }
    },

    /**
     * Get single chargeback detail
     */
    async fetchChargeback(scope, id) {
        const url = buildUrl(endpoints.chargeback_detail, scope, { id })
        return await client.get(url)
    },

    /**
     * Export chargebacks as Excel (admin-only)
     */
    async exportChargebacks(filters = {}) {
        return await client.download(endpoints.chargeback_export, filters)
    },

    /**
     * Search orders for chargeback creation
     * @param {Object} params - { order_number } or { email }
     */
    async searchOrdersForChargeback(params) {
        return await client.get(endpoints.chargeback_search, params)
    },

    /**
     * Create a new chargeback
     * @param {Object} data - { order, dispute_fee?, reason?, pertinence_date? }
     */
    async createChargeback(data) {
        return await client.post(endpoints.chargeback_create, data)
    },
}
